import React, { useContext, useMemo, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import {
  Anchor,
  Flex,
  IconCheckmarkCircle,
  IconClock,
  IconCommunity,
  IconWaiting,
  IconWarning,
  Menu,
  MenuItem,
  Tag,
  Toast,
  useToast,
} from '@zillow/constellation';
import { Box, Text, TextButton } from '@zillow/constellation-10';
import {
  AccountViewListingStatus,
  CometUserRoleType,
  useRentalManagerAccountViewZrmWebApiHandler_inquiries,
  useRentalManagerAccountViewZrmWebApiHandler_leases,
  useRentalManagerAccountViewZrmWebApiHandler_payments,
  useRentalManagerUserPropertyEditWebApiHandler_isApplicationsAccepted,
  useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get,
} from '@zg-rentals/rental-manager-env';

import { AppSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';

import PropertiesActions from '../../../actions/PropertiesActions';

import { RMWContext } from '../../../providers/RMWContext';
import stringHelper from '../../../utils/stringHelper';
import propertyUtils from '../../../utils/propertyUtils';
import { RMW_SUB_PATH } from '../../../pathsManifest';
import { getTagTypeByKey, STATUS_KEY } from '../../../utils/statusTagUtils';
import { escapeRoute, PANEL_ID } from '../../../utils/escapeRoute';
import PropertyConstants from '../../../constants/PropertyConstants';

import PropertyPanel from '../../../model/PropertyPanel';

import PropertiesPageCSEvents from '../../../model/ClickstreamEvent/PropertiesPageCSEvents';
import ZRAMPropertiesPageCSEvents from '../../../model/ClickstreamEvent/ZRAMPropertiesPageCSEvents';
import {
  ClaimDashboardIncreaseYourExposureEvent,
  ClaimDashboardReleaseClaimEvent,
} from '../../../model/ClickstreamEvent/ClaimDashboardEvents';

import ReleaseClaimModal from '../../Modal/ReleaseClaimModal';
import EnableApplicationsModal from '../../Modal/EnableApplicationsModal';

import {
  StyledAnchor,
  StyledBoneContainer,
  StyledCellAnchor,
  StyledContentDiv,
  StyledFlexContainer,
  StyledIconZillowLogo,
  StyledMenuPopper,
  StyledMenuPopperWrapper,
  StyledNotificationIcon,
  StyledPlaceholderDiv,
  StyledPropertyAddress,
  StyledPropertyPhoto,
  StyledPropertyPhotoContainer,
  StyledPropertyRow,
  StyledTableCell,
  StyledTag,
  StyledTagWrapper,
} from './StyledComponents';

const getCellDimensions = (isZram = true) => ({
  WIDTH: 200, // max-width of cell
  HEIGHT: isZram ? 96 : 63, // min-height of cell
});

import type {
  AccountViewTableCellCtaDto,
  AccountViewTableCellDto,
  WebAnalyticsLeaseDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { PanelProps } from '../../../model/PropertyPanel';
import type { PropertyProps } from '../../../model/Property';
import type { ZrmClickstreamListing } from '@zg-rentals/clickstream';

type PropertyRowProps = {
  listing: PropertyProps;
  showMoreListingAlias: string;
  setShowMoreListingAlias: (listingAlias: string) => void;
  isLeadsCountLoading: boolean;
  isTourCountLoading: boolean;
  onUpsellCellHover: (
    columnLabel: string,
    currentProperty: ZrmClickstreamListing,
    lease?: WebAnalyticsLeaseDto,
  ) => void;
};

const { PROPERTY_TABLE_COLUMNS, SYNDICATION_OVERVIEW_STATUS, PROPERTY_TABLE_TRIGGER_OBJECT_MAP } = PropertyConstants;

const PropertyRow = ({
  isTourCountLoading,
  isLeadsCountLoading,
  listing,
  showMoreListingAlias,
  setShowMoreListingAlias,
  onUpsellCellHover,
}: PropertyRowProps) => {
  const { paths } = useContext(RMWContext);
  const dispatch = useDispatch();
  const { enqueueToast } = useToast();

  // Dynamic cell dimensions for Text maxWidth (replaces StyledPropertyText)
  const cellWidth = getCellDimensions().WIDTH;
  const textMaxWidth = {
    base: `${cellWidth + 100}px`,
    lg: `${cellWidth}px`,
    xxl: `${cellWidth + 50}px`,
  };

  const isScreenMobile = useSelector((state) => AppSelectors.makeIsScreenMobile(state));
  const isScreenLgGTE = useSelector((state) => AppSelectors.makeIsScreenLgGTE(state));
  const isScreenXlGTE = useSelector((state) => AppSelectors.makeIsScreenXlGTE(state));

  const {
    canAccessDashboardRedesign,
    isEnterpriseCustomer,
    isPostingPathAdmin,
    leadCountByListingAlias,
    tourCountByListingAlias,
    user,
  } = useSelector((state) => {
    return {
      canAccessDashboardRedesign: state.app.canAccessDashboardRedesign,
      isEnterpriseCustomer: state.user.isEnterpriseCustomer,
      isPostingPathAdmin: state.user.isPostingPathAdmin,
      leadCountByListingAlias: state.properties.properties.leadCountByListingAlias,
      tourCountByListingAlias: state.properties.properties.tourCountByListingAlias,
      user: state.user,
    };
  }, shallowEqual);

  const show3D = isEnterpriseCustomer && isScreenXlGTE;
  const showContactInfo = isEnterpriseCustomer && isScreenLgGTE;
  const isMinWidthNeeded = !isEnterpriseCustomer && isScreenXlGTE;

  const {
    adproduct,
    photoUrls: { thumb },
    status: {
      message: statusMessage,
      text: listingStatusText,
      tagAppearance: listingStatusTagAppearance,
      status: listingStatus,
    },
    address,
    listingAlias,
    dateLastActivatedTimestamp,
    isMultifamily,
    name,
    feedName,
    feedId,
    panels,
    additionalListingSources,
    syndicationOverviewStatus,
    syndicationOverviewDisplayStatus,
    syndicationOverviewDisplayExplanation,
    activityMessage,
    userManagementCompanyId,
  } = listing;

  const { city, state, street, unit, zip } = address;
  const fullAddress = unit ? `${street} #${unit}` : street || name;
  const isZillow = feedName === 'Zillow Rental Manager';
  const tourCount = tourCountByListingAlias[listingAlias];
  const shouldHideColumnContent =
    listingStatus === AccountViewListingStatus.ARCHIVED || listingStatus === AccountViewListingStatus.UNABLE_TO_PUBLISH;

  const [displayReleaseClaimModal, setDisplayReleaseClaimModal] = useState(false);
  const [displayApplicationsModal, setDisplayApplicationsModal] = useState(false);

  const displayManagePermissions =
    user.roles.includes(CometUserRoleType.PROPERTY_MANAGER_COMPANY_ADMIN) &&
    user.userManagementCompanyId === userManagementCompanyId;

  const { data: paymentsData } = useRentalManagerAccountViewZrmWebApiHandler_payments(
    { listingAlias },
    {
      enabled: !isEnterpriseCustomer && !shouldHideColumnContent,
    },
  );
  const { data: inquiriesData, refetch: refetchInquiries } = useRentalManagerAccountViewZrmWebApiHandler_inquiries(
    { listingAlias },
    {
      enabled: !isEnterpriseCustomer && !shouldHideColumnContent,
    },
  );
  const { data: leasesData } = useRentalManagerAccountViewZrmWebApiHandler_leases(
    { listingAlias },
    {
      enabled: !isEnterpriseCustomer && !shouldHideColumnContent,
    },
  );

  const { data: { lease, ...webAnalyticsData } = {} } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get({
    listingAlias,
    includeLeases: true,
  });

  const { mutateAsync: setIsApplicationsAccepted } =
    useRentalManagerUserPropertyEditWebApiHandler_isApplicationsAccepted();

  const panelsToRender = useMemo(() => {
    const panelsToAdd: Array<PanelProps | null> = canAccessDashboardRedesign
      ? [
          PropertyPanel({
            id: dateLastActivatedTimestamp ? PANEL_ID.EDIT_LISTING : PANEL_ID.LIST,
            title: dateLastActivatedTimestamp ? 'Edit listing' : 'List',
            urlPath: `${paths.propertiesPage}/${listingAlias}/${
              dateLastActivatedTimestamp || isMultifamily ? 'listing' : 'create-listing'
            }`,
          }),
          isMultifamily
            ? null
            : PropertyPanel({
                id: PANEL_ID.OVERVIEW,
                title: 'Overview',
                urlPath: `${paths.propertiesPage}/${listingAlias}/${RMW_SUB_PATH.DASHBOARD}/overview`,
              }),
          displayManagePermissions
            ? PropertyPanel({
                id: PANEL_ID.MANAGE_PERMISSIONS,
                title: 'Manage Permissions',
                urlPath: `${paths.userManagementPage}/listing-permissions/${listingAlias}`,
              })
            : null,
        ]
      : [];

    const releaseClaimPanel = adproduct.isEligibleForReleaseClaim
      ? PropertyPanel({ id: PANEL_ID.RELEASE_CLAIM, title: 'Release claim' })
      : null;

    return [...panelsToAdd, ...panels, releaseClaimPanel].filter((item) => item && !item.isDisabled);
  }, [panels]);

  const targetListingRoute = useMemo(() => {
    if (isMultifamily) {
      return `${paths.propertiesPage}/${listingAlias}/listing`;
    }

    const panelToRender = panelsToRender[canAccessDashboardRedesign ? 1 : 0];
    return panelToRender ? panelToRender.urlPath : '';
  }, [canAccessDashboardRedesign, isMultifamily, listingAlias]);

  const routerHandler = (pathToRoute: string, openNewTab = false) => {
    if (openNewTab) {
      window.router.updateLocation(pathToRoute, '_blank');
    } else {
      window.router.transitionTo(pathToRoute);
    }
  };

  const handleRowClick = (event: React.MouseEvent<HTMLElement>) => {
    const openNewTab = event.ctrlKey || event.metaKey;
    event.preventDefault();
    event.stopPropagation();

    PropertiesPageCSEvents.PropertyCardClick({ user });

    dispatch(PropertiesActions.updateCurrentProperty({ ...address }));

    if (targetListingRoute) {
      routerHandler(targetListingRoute, openNewTab);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    PropertiesPageCSEvents.PropertyCardOverflowMenu({
      user,
      currentProperty: webAnalyticsData,
      lease,
    });
  };

  const handleMenuItemClick = ({ urlPath: url = '', id = '' }: PanelProps, event: React.MouseEvent<HTMLElement>) => {
    if (id === PANEL_ID.RELEASE_CLAIM) {
      setDisplayReleaseClaimModal(true);
      ClaimDashboardReleaseClaimEvent({ user });
      return;
    }

    if (id === PANEL_ID.OVERVIEW) {
      dispatch(PropertiesActions.updateCurrentProperty({ ...address }));
    }

    const openNewTab = event.ctrlKey || event.metaKey;

    if (escapeRoute(id)) {
      if (openNewTab) {
        window.router.updateLocation(url);
      } else {
        window.router.updateLocation(url, '_blank');
      }
    } else {
      routerHandler(url, openNewTab);
    }
  };

  const handleCellCtaClick = (
    event: React.MouseEvent<HTMLElement>,
    cta: Partial<AccountViewTableCellCtaDto>,
    label: string,
  ) => {
    event.stopPropagation();

    if (cta.text === 'Enable applications') {
      event.preventDefault();
      setDisplayApplicationsModal(true);
    }

    switch (label) {
      case PROPERTY_TABLE_COLUMNS.RENT:
        PropertiesPageCSEvents.PropertyCardPayments({
          user,
          currentProperty: webAnalyticsData,
          lease,
          triggerObject:
            PROPERTY_TABLE_TRIGGER_OBJECT_MAP.RENT_TRIGGERS[
              cta.text as keyof typeof PROPERTY_TABLE_TRIGGER_OBJECT_MAP.RENT_TRIGGERS
            ] || cta.text,
        });
        break;
      case PROPERTY_TABLE_COLUMNS.POTENTIAL_TENANTS:
        PropertiesPageCSEvents.PropertyCardPotentialTenants({
          user,
          currentProperty: webAnalyticsData,
          lease,
          triggerObject:
            PROPERTY_TABLE_TRIGGER_OBJECT_MAP.POTENTIAL_TENANTS_TRIGGERS[
              cta.text as keyof typeof PROPERTY_TABLE_TRIGGER_OBJECT_MAP.POTENTIAL_TENANTS_TRIGGERS
            ] || cta.text,
        });
        break;
      case PROPERTY_TABLE_COLUMNS.LEASES:
        PropertiesPageCSEvents.PropertyCardLeases({
          user,
          currentProperty: webAnalyticsData,
          lease,
          triggerObject:
            PROPERTY_TABLE_TRIGGER_OBJECT_MAP.LEASE_TRIGGERS[
              cta.text as keyof typeof PROPERTY_TABLE_TRIGGER_OBJECT_MAP.LEASE_TRIGGERS
            ] || cta.text,
        });
        break;
      default:
        break;
    }
  };

  const onEnableApplicationsClick = async () => {
    try {
      await setIsApplicationsAccepted({ getParams: { propertyId: listingAlias, isApplicationsAccepted: true } });
      await refetchInquiries();
      enqueueToast(
        <Toast
          appearance="success"
          body={
            <Text>
              Applications are enabled for {fullAddress}. You can change application preferences anytime on the
              property&apos;s&nbsp;
              <em>Applications</em>
              &nbsp;tab.
            </Text>
          }
        />,
      );
    } catch (error) {
      enqueueToast(
        <Toast appearance="error" body={<Text>There was an error enabling applications. Please try again.</Text>} />,
      );
    }
  };

  const menu = useMemo(() => {
    return (
      <Menu>
        {panelsToRender.map((item, index) => {
          if (item) {
            return (
              <MenuItem
                key={index}
                onClick={(event: React.MouseEvent<HTMLElement>) => handleMenuItemClick(item as PanelProps, event)}
                data-eventid={
                  item.id === PANEL_ID.RELEASE_CLAIM ? ClaimDashboardReleaseClaimEvent.data_event_id : undefined
                }
              >
                {item.title}
              </MenuItem>
            );
          }
        })}
      </Menu>
    );
  }, [panelsToRender, listingAlias]);

  const getStatusIcon = () => {
    const size = 'xs';

    switch (syndicationOverviewStatus) {
      case SYNDICATION_OVERVIEW_STATUS.PARTIALLY_PUBLISHED:
      case SYNDICATION_OVERVIEW_STATUS.PUBLISHED:
        return <IconCheckmarkCircle fontColor="green400" size={size} />;
      case SYNDICATION_OVERVIEW_STATUS.WAITLISTED:
        return <IconWaiting fontColor="grey400" size={size} />;
      case SYNDICATION_OVERVIEW_STATUS.PUBLISHING:
        return <IconClock fontColor="yellow400" size={size} />;
      case SYNDICATION_OVERVIEW_STATUS.DECLINED:
        return <IconWarning fontColor="red400" size={size} />;
      default:
        return null;
    }
  };

  const getWrappedText = (value: string) => {
    let characterLimit = isEnterpriseCustomer ? 25 : 30;
    characterLimit = isScreenXlGTE ? characterLimit : 23;
    const finalCharacterLimit = value.length > characterLimit ? characterLimit - 3 : characterLimit + 1;

    return stringHelper.wrapText(value, finalCharacterLimit);
  };

  const getPhotoContent = () => (
    <StyledPropertyPhotoContainer isZram={isEnterpriseCustomer}>
      <StyledPropertyPhoto
        primaryPhotoUrl={thumb}
        isZram={isEnterpriseCustomer}
        role="img"
        aria-label="Property photo"
      />
    </StyledPropertyPhotoContainer>
  );

  const getCellText = ({ status, description, cta }: Partial<AccountViewTableCellDto>, label: string) => (
    <>
      {status &&
        (status.tagAppearance ? (
          <StyledTag size="sm" appearance={status.tagAppearance}>
            {status.text}
          </StyledTag>
        ) : (
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            {status.url ? (
              <StyledCellAnchor
                href={status.url}
                onClick={(event: React.MouseEvent<HTMLElement>) => event.stopPropagation()}
              >
                {status.text}
              </StyledCellAnchor>
            ) : (
              <Text textStyle="body-sm" asChild css={{ textWrap: 'balance' }}>
                <p>{status.text}</p>
              </Text>
            )}
            {status.hasUnread && <StyledNotificationIcon />}
          </Box>
        ))}
      {description && (
        <Text textStyle="body-sm" css={{ color: status ? 'text.subtle' : 'text.neutral' }} asChild>
          <p>{description}</p>
        </Text>
      )}
      {cta && (
        <TextButton
          asChild={!!cta.url}
          onClick={(event: React.MouseEvent<HTMLElement>) => handleCellCtaClick(event, cta, label)}
          textStyle="body-sm-bold"
        >
          {cta.url ? <a href={cta.url || ''}>{cta.text}</a> : cta.text}
        </TextButton>
      )}
    </>
  );

  const getCellDataEventId = (label: string) => {
    if (label === PROPERTY_TABLE_COLUMNS.RENT) {
      return PropertiesPageCSEvents.PropertyCardPayments.data_event_id;
    }
    if (label === PROPERTY_TABLE_COLUMNS.POTENTIAL_TENANTS) {
      return PropertiesPageCSEvents.PropertyCardPotentialTenants.data_event_id;
    }
    if (label === PROPERTY_TABLE_COLUMNS.LEASES) {
      return PropertiesPageCSEvents.PropertyCardLeases.data_event_id;
    }
    return undefined;
  };

  const renderCellContent = (
    { status, description, cta, onlyDisplayOnHover }: Partial<AccountViewTableCellDto>,
    label: string,
  ) => {
    if (shouldHideColumnContent) {
      return <></>;
    }

    if (!status && !description && !cta) {
      return <>-</>;
    }

    if (onlyDisplayOnHover) {
      return (
        <>
          <StyledPlaceholderDiv data-eventdescription={cta?.text} data-eventid={getCellDataEventId(label)}>
            -
          </StyledPlaceholderDiv>
          <StyledContentDiv>{getCellText({ status, description, cta }, label)}</StyledContentDiv>
        </>
      );
    }

    return getCellText({ status, description, cta }, label);
  };

  const renderCell = (
    { status, description, cta, onlyDisplayOnHover }: Partial<AccountViewTableCellDto> = {},
    label: string,
  ) => {
    return (
      <StyledTableCell
        data-eventdescription="Property card hover"
        data-eventid={PropertiesPageCSEvents.PropertyCardHover.data_event_id}
        isZram={isEnterpriseCustomer}
        aria-labelledby={label}
        onMouseOver={() => {
          if (listingAlias && onlyDisplayOnHover) {
            onUpsellCellHover(label, webAnalyticsData, lease);
          }
        }}
      >
        {renderCellContent({ status, description, cta, onlyDisplayOnHover }, label)}
      </StyledTableCell>
    );
  };

  return (
    <>
      <StyledAnchor href={targetListingRoute} onClick={handleRowClick}>
        {/* we need to keep onClick handler or this will always reload the page  */}
        <StyledPropertyRow
          interactive={true}
          onClick={handleRowClick}
          data-testid="PropertyRow"
          isZram={isEnterpriseCustomer}
          data-eventid={PropertiesPageCSEvents.PropertyCardClick.data_event_id}
          data-eventdescription="Property card click"
        >
          {!isEnterpriseCustomer && (
            <>
              <StyledTableCell isZram={isEnterpriseCustomer} aria-labelledby={PROPERTY_TABLE_COLUMNS.ADDRESS}>
                <Flex display="flex" flexDirection="row">
                  {isScreenLgGTE && getPhotoContent()}
                  <StyledFlexContainer isZram={isEnterpriseCustomer}>
                    <StyledPropertyAddress>
                      {isPostingPathAdmin ? (
                        <Text textStyle="body-sm-bold" asChild css={{ textWrap: 'wrap !important' }}>
                          <p>{propertyUtils.formatAddress(address).formattedCompleteAddress}</p>
                        </Text>
                      ) : (
                        <Text textStyle="body-sm-bold" asChild css={{ maxWidth: textMaxWidth }}>
                          <p>{getWrappedText(isMultifamily && name ? name : fullAddress)}</p>
                        </Text>
                      )}
                      <Text textStyle="body-sm" asChild css={{ maxWidth: textMaxWidth }}>
                        <p>
                          {listingStatusTagAppearance ? (
                            <StyledTag
                              size="sm"
                              appearance={listingStatusTagAppearance}
                              icon={
                                listingStatus === AccountViewListingStatus.UNABLE_TO_PUBLISH ? <IconWarning /> : null
                              }
                            >
                              {listingStatusText}
                            </StyledTag>
                          ) : (
                            <Text textStyle="body-sm" color="text.subtle">
                              {listingStatusText}
                            </Text>
                          )}
                        </p>
                      </Text>
                    </StyledPropertyAddress>
                  </StyledFlexContainer>
                </Flex>
              </StyledTableCell>
              {renderCell(paymentsData, PROPERTY_TABLE_COLUMNS.RENT)}
              {renderCell(inquiriesData, PROPERTY_TABLE_COLUMNS.POTENTIAL_TENANTS)}
              {renderCell(leasesData, PROPERTY_TABLE_COLUMNS.LEASES)}
            </>
          )}

          {isEnterpriseCustomer && (
            <>
              {isScreenLgGTE && <StyledTableCell>{getPhotoContent()}</StyledTableCell>}
              <StyledTableCell>
                <StyledFlexContainer>
                  <Flex display="flex" flexDirection="column">
                    <StyledTagWrapper>
                      <Tag
                        appearance={getTagTypeByKey(STATUS_KEY.SYNDICATION_OVERVIEW_STATUS, syndicationOverviewStatus)}
                        icon={isMultifamily ? <IconCommunity /> : null}
                        size="sm"
                      >
                        <b>{statusMessage}</b>
                      </Tag>
                    </StyledTagWrapper>
                    <StyledPropertyAddress isMultifamily={isMultifamily}>
                      <Text textStyle="heading-xs" asChild css={{ maxWidth: textMaxWidth }}>
                        <p>{getWrappedText(isMultifamily && name ? name : fullAddress)}</p>
                      </Text>
                      <Text textStyle="body-sm" asChild css={{ maxWidth: textMaxWidth }}>
                        <p>
                          {isMultifamily ? (
                            <React.Fragment>
                              {fullAddress}
                              <br />
                            </React.Fragment>
                          ) : null}
                          {`${city}, ${state} ${zip}`}
                        </p>
                      </Text>
                    </StyledPropertyAddress>
                  </Flex>
                </StyledFlexContainer>
              </StyledTableCell>

              {show3D && (
                <StyledTableCell isMinWidthNeeded={isMinWidthNeeded}>
                  <StyledFlexContainer>
                    {isTourCountLoading ? (
                      <StyledBoneContainer dark={true} type="line" />
                    ) : (
                      <>
                        {tourCount > 0 ? (
                          <Text textStyle="body-sm" asChild css={{ maxWidth: textMaxWidth }}>
                            <p>
                              Added
                              {tourCount > 1 && ` (${tourCount})`}
                            </p>
                          </Text>
                        ) : (
                          <Text textStyle="body-sm" css={{ color: 'gray.400', maxWidth: textMaxWidth }} asChild>
                            <p>No tour</p>
                          </Text>
                        )}
                      </>
                    )}
                  </StyledFlexContainer>
                </StyledTableCell>
              )}
              {showContactInfo && (
                <>
                  <StyledTableCell>
                    <StyledFlexContainer>
                      <Text textStyle="body-sm" asChild css={{ maxWidth: textMaxWidth }}>
                        <p>
                          <strong>
                            {isZillow ? <StyledIconZillowLogo size="sm" /> : null}
                            {feedName || feedId}
                            &nbsp;
                            {additionalListingSources.length > 0
                              ? `(${stringHelper.pluralize(additionalListingSources.length, 'other', 'others')})`
                              : ''}
                          </strong>
                          <br />
                          {activityMessage}
                        </p>
                      </Text>
                    </StyledFlexContainer>
                  </StyledTableCell>
                  <StyledTableCell>
                    <StyledFlexContainer flexDirection="column" alignItems="flexStart" justifyContent="center">
                      <Text textStyle="body-sm-bold" asChild css={{ maxWidth: textMaxWidth }}>
                        <p>{adproduct.productTypesDisplayText.title}</p>
                      </Text>
                      <Text textStyle="body-sm-bold" css={{ maxWidth: textMaxWidth }}>
                        {adproduct.productTypesDisplayText.subtitle}
                      </Text>
                      {isLeadsCountLoading ? (
                        <StyledBoneContainer dark={true} width="80px" />
                      ) : (
                        <Text textStyle="body-sm" asChild css={{ maxWidth: textMaxWidth }}>
                          <p>{leadCountByListingAlias[listingAlias]}</p>
                        </Text>
                      )}
                      {adproduct.isEligibleForUpdate && (
                        <Text
                          textStyle="body-sm"
                          asChild
                          css={{ maxWidth: textMaxWidth }}
                          data-eventid={ZRAMPropertiesPageCSEvents.ChangeAdProduct.data_event_id}
                        >
                          <p>
                            <Anchor
                              target="_blank"
                              href={adproduct.updateCtaUrl}
                              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                                e.stopPropagation();
                                if (adproduct.updateCtaText === 'Increase your exposure') {
                                  ClaimDashboardIncreaseYourExposureEvent({ user });
                                }
                                ZRAMPropertiesPageCSEvents.ChangeAdProduct({ user });
                              }}
                              data-eventid={
                                adproduct.updateCtaText === 'Increase your exposure'
                                  ? ClaimDashboardIncreaseYourExposureEvent.data_event_id
                                  : undefined
                              }
                            >
                              {adproduct.updateCtaText}
                            </Anchor>
                          </p>
                        </Text>
                      )}
                    </StyledFlexContainer>
                  </StyledTableCell>
                </>
              )}

              <StyledTableCell isMinWidthNeeded={isMinWidthNeeded}>
                <StyledFlexContainer>
                  <Flex display="flex" flexDirection="column">
                    <Flex display="flex" alignItems="center">
                      {getStatusIcon()}
                      <Text textStyle="body-sm-bold" asChild css={{ marginLeft: '3px', maxWidth: textMaxWidth }}>
                        <p>{syndicationOverviewDisplayStatus}</p>
                      </Text>
                    </Flex>
                    {syndicationOverviewDisplayExplanation && (
                      <React.Fragment>{syndicationOverviewDisplayExplanation}</React.Fragment>
                    )}
                  </Flex>
                </StyledFlexContainer>
              </StyledTableCell>
            </>
          )}

          {!isScreenMobile && (
            <StyledTableCell
              onClick={handleMenuClick}
              isZram={isEnterpriseCustomer}
              aria-labelledby={PROPERTY_TABLE_COLUMNS.ACTIONS}
              data-eventid={PropertiesPageCSEvents.PropertyCardOverflowMenu.data_event_id}
            >
              <StyledFlexContainer display="flex" isZram={isEnterpriseCustomer}>
                <StyledMenuPopperWrapper isZram={isEnterpriseCustomer}>
                  <StyledMenuPopper
                    isOpen={showMoreListingAlias === listing.listingAlias}
                    onOpen={() => setShowMoreListingAlias(listing.listingAlias)}
                    triggered={menu}
                    isZram={isEnterpriseCustomer}
                  />
                </StyledMenuPopperWrapper>
              </StyledFlexContainer>
            </StyledTableCell>
          )}
        </StyledPropertyRow>
      </StyledAnchor>
      {displayReleaseClaimModal && (
        <ReleaseClaimModal
          listing={listing}
          listingRoute={targetListingRoute}
          onClose={() => setDisplayReleaseClaimModal(false)}
        />
      )}
      {displayApplicationsModal && (
        <EnableApplicationsModal
          setShowEnableApplicationsModal={setDisplayApplicationsModal}
          onConfirm={onEnableApplicationsClick}
        />
      )}
    </>
  );
};

export { PropertyRow };
export default PropertyRow;
